import { useMemo } from "react";
import { Link } from "react-router-dom";
import { useQuery } from "@tanstack/react-query";
import {
  FolderKanban, MapPin, Building2, Calendar, ChevronRight,
} from "lucide-react";
import { supabase } from "@/lib/supabase";
import { useRequireAuth } from "@/lib/auth";
import { useMyProjectIds } from "@/lib/useMyProjectIds";
import { Card, Pill, Spinner, EmptyState } from "@/components/ui";
import { format, parseISO } from "date-fns";

// =============================================================================
// FieldProjectsListScreen — /projects for field + subcontractor roles.
// "My jobs" view: only projects where the caller is on project_team. Scrubbed
// of financials — no contract value, margin, PO data etc. Manager+ users
// continue to get the full ProjectsListScreen via the App.tsx router switch.
// T-010 HOP Commit A (prereq for field-facing HOP upload flow).
// =============================================================================

interface ProjectRow {
  project_id:     string;
  project_code:   string;
  project_name:   string;
  status:         string;
  project_type:   string | null;
  scheduled_start_date: string | null;
  scheduled_end_date:   string | null;
  actual_start_date:    string | null;
  clients?:       { legal_name: string | null; client_code: string | null } | null;
  sites?:         { site_code: string | null; site_name: string | null; suburb: string | null; state: string | null } | null;
}

const STATUS_TONE: Record<string, "approved" | "pending" | "warning" | "default"> = {
  live:                 "approved",
  mobilising:           "pending",
  scheduled:            "default",
  acceptance_pending:   "warning",
  on_hold:              "warning",
  closed:               "default",
};

export default function FieldProjectsListScreen() {
  const user = useRequireAuth();

  // The union logic (project_team + crew -> project_crew_assignments) used to
  // be duplicated inline here, with its own date filter. When migration 327
  // gave ended stints a 14-day access tail, a second copy of the rule was one
  // copy too many: this screen would have LISTED a different set of projects
  // than RLS would then SERVE. useMyProjectIds is the single source of truth
  // for "what am I on" - the hook, the schedule and the database now agree.
  const { projectIds, isLoading: idsLoading } = useMyProjectIds(user);

  const { data: rows = [], isLoading: projectsLoading } = useQuery({
    queryKey: ["field_projects", projectIds.join(",")],
    enabled: !idsLoading,
    queryFn: async () => {
      if (projectIds.length === 0) return [];
      const { data, error } = await supabase
        .from("projects")
        .select(`
          project_id, project_code, project_name, status, project_type,
          scheduled_start_date, scheduled_end_date, actual_start_date,
          clients(legal_name, client_code),
          sites(site_code, site_name, suburb, state)
        `)
        .in("project_id", projectIds)
        .order("status", { ascending: true })
        .order("scheduled_start_date", { ascending: false });
      if (error) throw error;
      return (data ?? []) as unknown as ProjectRow[];
    },
  });
  const isLoading = idsLoading || projectsLoading;

  // -- Split active from finished for display -------------------------------
  const { active, done } = useMemo(() => {
    const a: ProjectRow[] = [];
    const d: ProjectRow[] = [];
    for (const r of rows) {
      if (r.status === "closed") d.push(r); else a.push(r);
    }
    return { active: a, done: d };
  }, [rows]);

  if (!user) return null;

  return (
    <div className="p-4 md:p-6 max-w-[1000px] mx-auto">
      <div className="mb-5">
        <h1 className="text-[22px] md:text-2xl font-semibold tracking-tight">My jobs</h1>
        <div className="text-text-muted text-[13px] mt-1">
          Projects you're assigned to. Tap through for the brief, documents, and HOP uploads.
        </div>
      </div>

      {isLoading && (
        <div className="py-16 flex justify-center"><Spinner size={20} /></div>
      )}

      {!isLoading && rows.length === 0 && (
        <Card>
          <EmptyState
            title="No projects assigned yet"
            description="Once a manager adds you to a project team, it'll show up here. Check with your supervisor if you think this is a mistake."
            icon={FolderKanban}
          />
        </Card>
      )}

      {!isLoading && active.length > 0 && (
        <>
          <SectionHead label="Active" count={active.length} />
          <div className="space-y-3 mb-6">
            {active.map(p => <ProjectCard key={p.project_id} project={p} />)}
          </div>
        </>
      )}

      {!isLoading && done.length > 0 && (
        <>
          <SectionHead label="Finished" count={done.length} muted />
          <div className="space-y-3">
            {done.map(p => <ProjectCard key={p.project_id} project={p} />)}
          </div>
        </>
      )}
    </div>
  );
}

function SectionHead({ label, count, muted }: { label: string; count: number; muted?: boolean }) {
  return (
    <div className="flex items-center gap-3 mb-3">
      <div className={`h-1 w-10 rounded-full ${muted ? "bg-line" : "bg-accent"}`} />
      <div className="text-text-muted text-[11px] tracking-wider2 uppercase font-mono">
        {label} · {count}
      </div>
    </div>
  );
}

function ProjectCard({ project }: { project: ProjectRow }) {
  const tone = STATUS_TONE[project.status] ?? "default";
  const clientName = project.clients?.legal_name ?? "—";
  const siteBits = [project.sites?.site_name, project.sites?.suburb, project.sites?.state].filter(Boolean).join(" · ");
  const startDate = project.actual_start_date ?? project.scheduled_start_date;
  const endDate   = project.scheduled_end_date;

  return (
    <Link to={`/projects/${project.project_id}`} className="block">
      <Card className="hover:border-line-light transition-colors">
        <div className="p-4 flex items-start gap-3">
          <div className="w-10 h-10 rounded-md bg-accent/10 text-accent flex items-center justify-center border border-accent/30 flex-shrink-0">
            <FolderKanban size={18} />
          </div>
          <div className="flex-1 min-w-0">
            <div className="flex items-start justify-between gap-2 flex-wrap">
              <div className="min-w-0">
                <div className="text-[11px] font-mono text-text-muted">{project.project_code}</div>
                <div className="text-text font-medium truncate">{project.project_name}</div>
              </div>
              <Pill label={project.status} tone={tone} small />
            </div>
            <div className="mt-2 flex items-center gap-3 flex-wrap text-[12px] text-text-muted">
              <span className="inline-flex items-center gap-1"><Building2 size={11} /> {clientName}</span>
              {siteBits && <span className="inline-flex items-center gap-1"><MapPin size={11} /> {siteBits}</span>}
              {startDate && (
                <span className="inline-flex items-center gap-1">
                  <Calendar size={11} />
                  {format(parseISO(startDate), "dd MMM yy")}
                  {endDate ? ` – ${format(parseISO(endDate), "dd MMM yy")}` : ""}
                </span>
              )}
            </div>
          </div>
          <ChevronRight size={16} className="text-text-muted flex-shrink-0 mt-1" />
        </div>
      </Card>
    </Link>
  );
}
